import React from 'react';

import {AssignmentsList} from 'client/TeacherDashboard/Assignments/AssignmentsList';
import {ClassDetailEntryFlows} from 'client/TeacherDashboard/ClassDetail/ClassDetailEntryFlows';
import {Overview} from 'client/TeacherDashboard/ClassDetail/Overview/Overview';
import {People} from 'client/TeacherDashboard/ClassDetail/People/People';
import {Settings} from 'client/TeacherDashboard/ClassDetail/Settings/Settings';
import {ClassSubjects} from 'client/TeacherDashboard/ClassDetail/Subjects/ClassSubjects';
import {useStoreChangePump} from 'client/TeacherDashboard/ClassDetail/useStoreChangePump';
import {DashboardHeader} from 'client/TeacherDashboard/Layout/DashboardHeader';
import {TeacherDashboardLayout} from 'client/TeacherDashboard/Layout/TeacherDashboardLayout';
import {useHasTeacherDashboard} from 'client/components/FeatureFlag/useHasTeacherDashboard';

import Classrooms from './Classrooms.react';
import SecondaryHeader from './shared/SecondaryHeader';

interface Props {
  children?: React.ReactNode;
  location?: {pathname?: string};
  params?: {classId?: string};
  [routeProp: string]: unknown;
}

const CLASS_ASSIGNMENTS_PATH = /^\/classes\/[^/]+\/assignments\/?$/;
// Anchored to the class overview only — must NOT swallow the per-student /students/detail/:id route.
const CLASS_OVERVIEW_PATH = /^\/classes\/[^/]+\/students\/overview\/?$/;
const CLASS_SETTINGS_PATH = /^\/classes\/[^/]+\/settings(?:\/|$)/;
// Subtree form so the legacy /people/students/{enrolled,invited} + /coteachers/approved sub-paths
const CLASS_PEOPLE_PATH = /^\/classes\/[^/]+\/people(?:\/|$)/;
const CLASS_SUBJECTS_PATH = /^\/classes\/[^/]+\/subjects\/?$/;
// The student my-assignments view stays on legacy chrome.
const STUDENT_ASSIGNMENTS_PATH = /\/my-assignments\/?$/;

// v2-rebuilt tabs render inline, keyed per class; ClassDetailEntryFlows (below) awaits the classroom once.
const INLINE_TABS: {match: RegExp; render: (classroomId: string) => React.ReactNode}[] = [
  {
    match: CLASS_ASSIGNMENTS_PATH,
    render: (classroomId) => <AssignmentsList classroomId={classroomId} />
  },
  {
    match: CLASS_OVERVIEW_PATH,
    render: (classroomId) => <Overview classroomId={classroomId} key={`overview-${classroomId}`} />
  },
  {
    match: CLASS_SETTINGS_PATH,
    render: (classroomId) => <Settings classroomId={classroomId} key={`settings-${classroomId}`} />
  },
  {
    match: CLASS_PEOPLE_PATH,
    render: (classroomId) => <People classroomId={classroomId} key={`people-${classroomId}`} />
  },
  {
    match: CLASS_SUBJECTS_PATH,
    render: (classroomId) => (
      <ClassSubjects classroomId={classroomId} key={`subjects-${classroomId}`} />
    )
  }
];

// Re-renders a passive-Mandark legacy tab on store changes; the fresh clone each tick defeats
// React's stable-children bailout at the router boundary.
const ClassDetailContent = ({children}: {children: React.ReactNode}) => {
  useStoreChangePump();
  return (
    <div className='pt-x4'>
      {React.isValidElement(children) ? React.cloneElement(children) : children}
    </div>
  );
};

// Chrome for /classes: flag-off teachers, the student my-assignments view, and non-class paths keep
// legacy chrome; flag-on teachers get the v2 shell (rebuilt tabs inline, the rest via ClassDetailContent).
const ClassroomsChromeWrapper = ({children, ...rest}: Props) => {
  const {hasTeacherDashboard} = useHasTeacherDashboard();

  const pathname: string = rest.location?.pathname ?? '';
  const classroomId = rest.params?.classId;

  const legacyChrome = (
    <>
      <SecondaryHeader {...rest} aria-label='secondary' />
      <Classrooms {...rest}>{children}</Classrooms>
    </>
  );

  if (!hasTeacherDashboard || !classroomId || STUDENT_ASSIGNMENTS_PATH.test(pathname)) {
    return legacyChrome;
  }

  const inlineTab = INLINE_TABS.find(({match}) => match.test(pathname));

  return (
    <TeacherDashboardLayout header={<DashboardHeader pathname={pathname} />} pathname={pathname}>
      {inlineTab ? (
        <>
          <ClassDetailEntryFlows classroomId={classroomId} key={`entry-${classroomId}`} />
          {inlineTab.render(classroomId)}
        </>
      ) : (
        <ClassDetailContent key={pathname}>{children}</ClassDetailContent>
      )}
    </TeacherDashboardLayout>
  );
};

export default ClassroomsChromeWrapper;
